import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, Upload, UserPlus, Lock } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";

const input = "w-full bg-white border border-brand-border rounded-lg px-4 py-3 text-sm text-brand-ink placeholder:text-brand-tertiary focus:outline-none focus:border-brand-ink transition-colors";
const EMPTY = { first_name: "", last_name: "", email: "" };

function IdPicker({ file, setFile }) {
  const ref = useRef(null);
  return (
    <div>
      <input ref={ref} type="file" accept="image/*,.pdf,.heic,.heif" className="hidden" data-testid="onb-public-id-input"
        onChange={(e) => setFile(e.target.files?.[0] || null)} />
      <button type="button" onClick={() => ref.current?.click()} data-testid="onb-public-id-btn"
        className={`w-full flex items-center gap-3 border border-dashed rounded-lg px-4 py-4 text-left transition-colors ${file ? "border-emerald-400 bg-emerald-50/50" : "border-brand-border hover:border-brand-ink bg-white"}`}>
        <Upload className="h-5 w-5 text-brand-tertiary shrink-0" />
        <span className="text-sm">
          {file ? <span className="text-brand-ink">{file.name}</span> : <><span className="text-brand-ink">Upload a copy of your ID</span><br /><span className="text-xs text-brand-muted">Driver's license or passport. Photo or PDF, up to 15MB</span></>}
        </span>
      </button>
    </div>
  );
}

function PersonForm({ token, onDone }) {
  const [f, setF] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    if (!file) return setErr("Please upload a copy of your ID.");
    setBusy(true); setErr(null);
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => fd.append(k, v));
    fd.append("id_file", file);
    try { const { data } = await api.post(`/public/onboarding/${token}/members`, fd); setF(EMPTY); setFile(null); onDone(data.name); }
    catch (e2) { setErr(formatApiErrorDetail(e2.response?.data?.detail) || "Something went wrong. Please try again."); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="onb-public-form">
      <div className="grid sm:grid-cols-2 gap-3">
        <input required placeholder="First name" autoComplete="given-name" value={f.first_name} onChange={set("first_name")} data-testid="onb-public-first" className={input} />
        <input required placeholder="Last name" autoComplete="family-name" value={f.last_name} onChange={set("last_name")} data-testid="onb-public-last" className={input} />
      </div>
      <div>
        <input required type="email" placeholder="Work email" autoComplete="email" value={f.email} onChange={set("email")} data-testid="onb-public-email" className={input} />
        <p className="text-xs text-brand-muted mt-1.5 px-1" data-testid="onb-public-email-hint">Please use your work email (not a personal one). We use it for your member account and building access.</p>
      </div>
      <IdPicker file={file} setFile={setFile} />
      {err && <p className="text-sm text-rose-700" data-testid="onb-public-error">{err}</p>}
      <button type="submit" disabled={busy} data-testid="onb-public-submit" className="w-full bg-brand-ink text-white rounded-full py-3 text-sm tracking-wide hover:opacity-90 transition-opacity disabled:opacity-50">
        {busy ? "Submitting…" : "Submit"}
      </button>
    </form>
  );
}

export default function OnboardPublic() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [justAdded, setJustAdded] = useState(null);
  const load = () => api.get(`/public/onboarding/${token}`).then(({ data }) => setInfo(data))
    .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || "This link isn't available."));
  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen bg-brand-page flex flex-col">
      <header className="px-6 sm:px-12 py-6 border-b border-brand-border"><p className="font-serif text-xl text-brand-ink">Bond Collective</p></header>
      <main className="flex-1 w-full max-w-xl mx-auto px-6 py-12">
        {error ? (
          <div className="text-center space-y-3 pt-12" data-testid="onb-public-unavailable">
            <Lock className="h-8 w-8 mx-auto text-brand-tertiary" />
            <p className="text-brand-ink">{error}</p>
          </div>
        ) : !info ? <div className="h-64 rounded-xl bg-brand-hover animate-pulse" /> : (
          <div className="space-y-8">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-brand-tertiary">{info.location}</p>
              <h1 className="font-serif text-4xl sm:text-5xl font-light tracking-tight text-brand-ink mt-2" data-testid="onb-public-title">Welcome, {info.company_name}</h1>
              <p className="text-sm text-brand-muted mt-3">Please add each person on your team who will use the space. We use this to set up building access and your member accounts.</p>
            </div>
            {justAdded ? (
              <div className="border border-emerald-200 bg-emerald-50 rounded-xl p-6 space-y-4 text-center" data-testid="onb-public-success">
                <CheckCircle2 className="h-8 w-8 mx-auto text-emerald-700" />
                <p className="text-brand-ink">Thanks, {justAdded} is all set.</p>
                <button onClick={() => setJustAdded(null)} data-testid="onb-public-add-another" className="inline-flex items-center gap-1.5 border border-brand-ink rounded-full px-5 py-2 text-sm text-brand-ink hover:bg-white transition-colors"><UserPlus className="h-4 w-4" /> Add another team member</button>
              </div>
            ) : <PersonForm token={token} onDone={(n) => { setJustAdded(n); load(); }} />}
            {info.team.length > 0 && (
              <div data-testid="onb-public-team">
                <p className="text-[10px] uppercase tracking-[0.15em] text-brand-tertiary mb-2">Already added ({info.team.length})</p>
                <div className="flex flex-wrap gap-2">{info.team.map((n, i) => <span key={i} className="text-xs bg-white border border-brand-border rounded-full px-3 py-1 text-brand-ink">{n}</span>)}</div>
              </div>
            )}
            <p className="text-xs text-brand-tertiary" data-testid="onb-public-privacy">Your ID is stored securely and only shared with the Bond Collective and Building Security (if applicable) teams at your location.</p>
          </div>
        )}
      </main>
    </div>
  );
}
